import { Button, DatePicker, Drawer, Input, Select } from 'antd';
import dayjs from 'dayjs';
import sortBy from 'lodash/sortBy';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { DATE_BACKEND_FORMAT, DAY_FORMAT } from '../../../constants';
import { getAllPartnerItemTypes, getMyPartnerItemTypes, getPartners } from '../../../modules/partners/api';
import { getMySites, listSitesForPartner } from '../../../modules/sites/api';
import { getDrivers } from '../../../modules/drivers/api';
import { tenantConfig } from '../../../config/tenantConfig';

const { RangePicker } = DatePicker;

const OrdersFilter = ({
  open,
  onClose,
  query,
  onApply,
  hideDriver,
  hidePartner: hidePartnerProp,
  hideDateRange,
  fixedPartnerId,
  // #159 — partner portal: item types + sites come from /partners/me/… (the
  // admin endpoints answer 403 to a viewer, which left both filters empty).
  viewOnly,
}) => {
  // OVO-132 — a single-tenant instance never shows the partner picker.
  const hidePartner = hidePartnerProp || tenantConfig.isSingleTenant;
  const { t } = useTranslation('orders');
  const { t: tc } = useTranslation('common');
  const [partnerOptions, setPartnerOptions] = useState([]);
  const [itemTypeOptions, setItemTypeOptions] = useState([]); // OVO-019

  // Local filter state
  const [name, setName] = useState('');
  const [pickupName, setPickupName] = useState('');
  const [routeId, setRouteId] = useState('');
  const [partnerOrderRef, setPartnerOrderRef] = useState('');
  const [orderRef, setOrderRef] = useState('');
  const [status, setStatus] = useState(undefined);
  const [dateRange, setDateRange] = useState(null);
  // #173 — orders on a route of this driver (admin lists only: `hideDriver`
  // on the partner portal, which must not load /drivers — OVO-002 / #159).
  const [driverId, setDriverId] = useState(undefined);
  const [driverOptions, setDriverOptions] = useState([]);
  const [partnerId, setPartnerId] = useState(undefined);
  const [orderType, setOrderType] = useState(undefined);
  const [issueTypes, setIssueTypes] = useState([]);
  const [partnerItemTypeIds, setPartnerItemTypeIds] = useState([]); // OVO-019
  // OVO-101 — Site filter: scoped to the currently-selected partner. The
  // dropdown is hidden when no partner is selected (cross-partner Site
  // search isn't useful — different partners can have the same name).
  const [siteId, setSiteId] = useState(undefined);
  const [siteOptions, setSiteOptions] = useState([]);

  const filterOption = (input, option = null) => (option?.label ?? '').toLowerCase()?.includes(input.toLowerCase());

  useEffect(() => {
    if (hideDriver || !open || driverOptions.length > 0) return;
    getDrivers({ take: 1000 }).then(({ response }) => {
      const drivers = response?.drivers || [];
      setDriverOptions(
        sortBy(drivers, (d) => (d.name || '').toLowerCase()).map((d) => ({ label: d.name, value: String(d.id) })),
      );
    });
  }, [hideDriver, open, driverOptions.length]);

  useEffect(() => {
    if (hidePartner) return;
    const loadPartners = async () => {
      const { response } = await getPartners({ take: 200 });
      if (response?.partners) {
        setPartnerOptions(response.partners.map((p) => ({ label: p.name, value: String(p.id) })));
      }
    };
    loadPartners();
  }, [hidePartner]);

  // OVO-019 — load all partner item types for the Product Type filter.
  // Label disambiguates by partner name when no partner is preselected.
  useEffect(() => {
    const loadItemTypes = async () => {
      const { response } = viewOnly ? await getMyPartnerItemTypes() : await getAllPartnerItemTypes();
      if (Array.isArray(response)) {
        setItemTypeOptions(
          response.map((t) => ({
            label: hidePartner || partnerId ? t.name : `${t.name} — ${t.partner?.name || ''}`,
            value: String(t.id),
            partnerId: t.partner?.id ?? t.partnerId,
            category: t.category,
          })),
        );
      }
    };
    loadItemTypes();
  }, [hidePartner, partnerId, viewOnly]);

  // OVO-101 — load sites for the active partner (autocomplete options
  // for the Site filter). The "active partner" is either the in-drawer
  // selection (global Orders page) or the fixed parent context
  // (PartnerDetail's Orders tab, which hides the Partner dropdown).
  // Wipes when neither is set so the dropdown stays hidden.
  const activePartnerIdForSites = partnerId || fixedPartnerId;
  useEffect(() => {
    if (!activePartnerIdForSites) {
      setSiteOptions([]);
      return;
    }
    const loadSites = async () => {
      const { response } = viewOnly ? await getMySites() : await listSitesForPartner(activePartnerIdForSites);
      if (Array.isArray(response)) {
        setSiteOptions(
          response
            .filter((s) => !s.isArchived)
            .map((s) => ({
              label: `${s.name} — ${s.address}`,
              value: String(s.id),
            })),
        );
      }
    };
    loadSites();
  }, [activePartnerIdForSites, viewOnly]);

  // Sync local state from query when drawer opens
  useEffect(() => {
    if (open) {
      setName(query.name || '');
      setPickupName(query.pickupName || '');
      setRouteId(query.routeId || '');
      setPartnerOrderRef(query.partnerOrderRef || '');
      setOrderRef(query.orderRef || '');
      setStatus(query.status || undefined);
      setDateRange(
        query.deliveryDateFrom || query.deliveryDateTo
          ? [
              query.deliveryDateFrom ? dayjs(query.deliveryDateFrom, DATE_BACKEND_FORMAT) : null,
              query.deliveryDateTo ? dayjs(query.deliveryDateTo, DATE_BACKEND_FORMAT) : null,
            ]
          : null,
      );
      setDriverId(query.driverId ? String(query.driverId) : undefined);
      setPartnerId(query.partnerId || undefined);
      setOrderType(query.orderType || undefined);
      setIssueTypes(query.issueTypes ? query.issueTypes.split(',') : []);
      setPartnerItemTypeIds(query.partnerItemTypeIds ? query.partnerItemTypeIds.split(',') : []);
      setSiteId(query.siteId ? String(query.siteId) : undefined);
    }
  }, [open, query]);

  const handleApply = () => {
    const params = { ...query };
    params.name = name || undefined;
    params.pickupName = pickupName || undefined;
    params.routeId = routeId || undefined;
    params.partnerOrderRef = partnerOrderRef || undefined;
    params.orderRef = orderRef || undefined;
    params.status = status || undefined;
    params.deliveryDateFrom = dateRange && dateRange[0] ? dateRange[0].format(DATE_BACKEND_FORMAT) : undefined;
    params.deliveryDateTo = dateRange && dateRange[1] ? dateRange[1].format(DATE_BACKEND_FORMAT) : undefined;
    params.driverId = hideDriver ? undefined : driverId || undefined;
    params.partnerId = partnerId || undefined;
    params.orderType = orderType || undefined;
    params.issueTypes = Array.isArray(issueTypes) && issueTypes.length > 0 ? issueTypes.join(',') : undefined;
    params.partnerItemTypeIds =
      Array.isArray(partnerItemTypeIds) && partnerItemTypeIds.length > 0 ? partnerItemTypeIds.join(',') : undefined;
    params.siteId = siteId || undefined;
    params.current = 1;
    onApply(params, true);
    onClose();
  };

  const handleClear = () => {
    setName('');
    setPickupName('');
    setRouteId('');
    setPartnerOrderRef('');
    setOrderRef('');
    setStatus(undefined);
    setDateRange(null);
    setDriverId(undefined);
    setPartnerId(undefined);
    setOrderType(undefined);
    setIssueTypes([]);
    setPartnerItemTypeIds([]);
    setSiteId(undefined);
    onApply({ current: 1 }, true);
    onClose();
  };

  return (
    <Drawer
      title={t('filterOrders')}
      placement="right"
      onClose={onClose}
      open={open}
      zIndex={1010}
      width={360}
      footer={
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
          <Button onClick={handleClear}>{tc('clear')}</Button>
          <Button type="primary" onClick={handleApply}>
            {tc('apply')}
          </Button>
        </div>
      }
    >
      <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
        <div>
          <label style={{ display: 'block', marginBottom: 4, fontWeight: 500 }}>{tc('name')}</label>
          <Input placeholder={t('orderName')} value={name} onChange={(e) => setName(e.target.value)} allowClear />
        </div>
        <div>
          <label style={{ display: 'block', marginBottom: 4, fontWeight: 500 }}>{t('pickupName')}</label>
          <Input
            placeholder={t('pickupNameFilter')}
            value={pickupName}
            onChange={(e) => setPickupName(e.target.value)}
            allowClear
          />
        </div>
        <div>
          <label style={{ display: 'block', marginBottom: 4, fontWeight: 500 }}>{t('routeId')}</label>
          <Input placeholder={t('routeId')} value={routeId} onChange={(e) => setRouteId(e.target.value)} allowClear />
        </div>
        <div>
          <label style={{ display: 'block', marginBottom: 4, fontWeight: 500 }}>
            {t('partnerOrderRefFilter')}
            <span style={{ fontWeight: 400, color: 'var(--text-muted)', fontSize: 12, marginLeft: 4 }}>
              {t('partnerOrderRefFilterHint')}
            </span>
          </label>
          <Input
            placeholder={t('partnerOrderRefFilter')}
            value={partnerOrderRef}
            onChange={(e) => setPartnerOrderRef(e.target.value)}
            allowClear
          />
        </div>
        <div>
          <label style={{ display: 'block', marginBottom: 4, fontWeight: 500 }}>
            {t('orderRef')}
            <span style={{ fontWeight: 400, color: 'var(--text-muted)', fontSize: 12, marginLeft: 4 }}>
              {t('orderRefFilterHint')}
            </span>
          </label>
          <Input
            placeholder={t('orderRef')}
            value={orderRef}
            onChange={(e) => setOrderRef(e.target.value)}
            allowClear
          />
        </div>
        <div>
          <label style={{ display: 'block', marginBottom: 4, fontWeight: 500 }}>{tc('status')}</label>
          <Select
            style={{ width: '100%' }}
            placeholder={t('allStatuses')}
            value={status}
            onChange={(val) => setStatus(val)}
            allowClear
            // Status cleanup (Seb, Aug 2026): the map carries every HISTORICAL
            // label (needed to render old logs), but five of its keys are not
            // even in the OrderStatus enum (PENDING/OPTIMIZED/DISPATCHED/
            // SERVICE_ARRIVED/IN_PROGRESS) and four more have never occurred
            // in prod (DRAFT/UNSCHEDULED/GOING_NEXT/IN_TRANSIT). Offering them
            // here produced filters that could never match anything.
            options={[
              'UNASSIGNED',
              'ASSIGNED',
              'PICKUP_ARRIVED',
              'PICKUP_FAILED',
              'COLLECTED',
              'DELIVERY_ARRIVED',
              'DELIVERY_FAILED',
              'SKIPPED',
              'COMPLETED',
              'CANCELLED',
              'GEOCODING',
              'ERROR',
            ].map((key) => ({
              label: tc(`orderStatuses.${key}`),
              value: key,
            }))}
          />
        </div>
        {/* OVO-026 — hidden on the route-plan Orders view, which is
            already scoped to a single date via the URL ?date= param.
            Exposing the range picker there is confusing (the underlying
            data is loaded for one day only). */}
        {!hideDateRange && (
          <div>
            <label style={{ display: 'block', marginBottom: 4, fontWeight: 500 }}>{t('deliveryDate')}</label>
            <RangePicker
              style={{ width: '100%' }}
              value={dateRange}
              onChange={(dates) => setDateRange(dates)}
              format={DAY_FORMAT}
              placeholder={[tc('from'), tc('to')]}
              allowEmpty={[true, true]}
            />
          </div>
        )}
        {!hideDriver && (
          <div>
            <label style={{ display: 'block', marginBottom: 4, fontWeight: 500 }}>{t('driver')}</label>
            <Select
              style={{ width: '100%' }}
              placeholder={t('allDrivers')}
              value={driverId}
              onChange={(val) => setDriverId(val)}
              allowClear
              showSearch
              filterOption={filterOption}
              options={driverOptions}
              data-testid="orders-filter-driver"
            />
          </div>
        )}
        {!hidePartner && (
          <div>
            <label style={{ display: 'block', marginBottom: 4, fontWeight: 500 }}>{t('partner')}</label>
            <Select
              style={{ width: '100%' }}
              placeholder={t('allPartners')}
              value={partnerId}
              onChange={(val) => setPartnerId(val)}
              allowClear
              showSearch
              filterOption={filterOption}
              options={partnerOptions}
            />
          </div>
        )}
        <div>
          <label style={{ display: 'block', marginBottom: 4, fontWeight: 500 }}>{t('orderType')}</label>
          <Select
            style={{ width: '100%' }}
            placeholder={t('allTypes')}
            value={orderType}
            onChange={(val) => setOrderType(val)}
            allowClear
            options={Object.keys(tc('orderTypes', { returnObjects: true })).map((key) => ({
              label: tc(`orderTypes.${key}`),
              value: key,
            }))}
          />
        </div>
        <div>
          <label style={{ display: 'block', marginBottom: 4, fontWeight: 500 }}>{t('issueType')}</label>
          <Select
            style={{ width: '100%' }}
            placeholder={t('selectIssueTypes')}
            value={issueTypes}
            onChange={(val) => setIssueTypes(val)}
            mode="multiple"
            allowClear
            showSearch
            filterOption={filterOption}
            options={Object.keys(tc('issueTypes', { returnObjects: true })).map((key) => ({
              value: key,
              label: tc(`issueTypes.${key}`),
            }))}
          />
        </div>
        <div>
          {/* OVO-019 — multi-select of partner item types. When a partner is
              selected we scope the options to that partner; otherwise we
              show all with a "Name — Partner" label. */}
          <label style={{ display: 'block', marginBottom: 4, fontWeight: 500 }}>{t('productType')}</label>
          <Select
            style={{ width: '100%' }}
            placeholder={t('selectProductTypes')}
            value={partnerItemTypeIds}
            onChange={(val) => setPartnerItemTypeIds(val)}
            mode="multiple"
            allowClear
            showSearch
            filterOption={filterOption}
            options={itemTypeOptions.filter((o) => !partnerId || String(o.partnerId) === String(partnerId))}
          />
        </div>
        {/* OVO-101 — Site (Address Book) filter. Visible when there's an
            active partner context — either the in-drawer Partner pick
            (global Orders page) or the fixed parent (PartnerDetail's
            Orders tab, which hides the Partner dropdown entirely). */}
        {activePartnerIdForSites && (
          <div>
            <label style={{ display: 'block', marginBottom: 4, fontWeight: 500 }}>{t('site')}</label>
            <Select
              style={{ width: '100%' }}
              placeholder={t('selectSite')}
              value={siteId}
              onChange={(val) => setSiteId(val)}
              allowClear
              showSearch
              filterOption={filterOption}
              options={siteOptions}
              notFoundContent={siteOptions.length === 0 ? t('noSitesForPartner') : null}
            />
          </div>
        )}
      </div>
    </Drawer>
  );
};

export default OrdersFilter;
